import { Box, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { backendService } from "@/services/backend.service";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { isValidEmail, validatePhoneNumber } from "@/utils/phoneAndEmailUtils";
import { SigninLayout } from "./common";

const useCreateAccount = () => {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const navigate = useNavigate();
	const [accountExistsError, setAccountExistsError] = useState<string>();

	return {
		...useMutation({
			mutationFn: (data: { name: string; facility: string; email: string; phone: string; notes: string }) => backendService.createAccountRequest(data),
			onSuccess: (_, data) => {
				track("create_account_success", data);
				showSnackbar(t("account.creation.request.sent.successfully"), "success");
				setAccountExistsError(undefined);
				navigate("/");
			},
			onError: (error: any) => {
				// Check if it's a 409 Conflict (account already exists)
				if (error?.response?.status === 409) {
					track("create_account_user_exists", error?.response?.data);
					setAccountExistsError(error?.response?.data?.detail || "An account with this email or phone number already exists.");
				} else {
					setAccountExistsError(undefined);
					showErrorSnackbar("Failed to send account creation request. Please try again.", {}, error);
				}
			},
		}),
		accountExistsError,
	};
};

export const CreateAccount: FC = () => {
	const { mutateAsync: createAccount, isPending, accountExistsError } = useCreateAccount();

	const [name, setName] = useState<string>("");
	const [facility, setFacility] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [phone, setPhone] = useState<string>("");
	const [notes, setNotes] = useState<string>("");

	const isEmailValid = useMemo<boolean>(() => isValidEmail(email), [email]);
	const isPhoneValid = useMemo<boolean>(() => validatePhoneNumber(phone).isValid, [phone]);

	const isFormValid = name && facility && (isEmailValid || isPhoneValid);

	return (
		<SigninLayout title="Please Enter your details">
			<SystemValidationTextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} required />
			<SystemValidationTextField label="Facility" value={facility} onChange={(e) => setFacility(e.target.value)} required />
			<SystemValidationTextField label="Email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase().trim())} />
			<SystemValidationTextField label="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} />
			<SystemValidationTextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
			{accountExistsError && (
				<Box sx={{ mt: 2, p: 2, opacity: 0.7, bgcolor: "#08d472ff", borderRadius: 1, border: "1px solid lime" }}>
					<Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 1 }}>{accountExistsError}</Typography>
				</Box>
			)}
			<SystemButton sx={{ width: "100%" }} onClick={() => createAccount({ name, facility, email, phone, notes })} loading={isPending} disabled={!isFormValid}>
				Send
			</SystemButton>
		</SigninLayout>
	);
};
